import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Rating from '@mui/material/Rating';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import { useQuery } from '@tanstack/react-query';
import type { Review } from '@/types';
import { guidesApi, guidesKeys } from '@/features/guides/api/guides.api';
import { GuideReviewListItem } from '@/features/guide/components/GuideReviewListItem';
import { RatingBreakdown } from '@/features/guide/components/RatingBreakdown';

interface GuidePublicReviewsDialogProps {
  profileId: string | null;
  guideName?: string;
  open: boolean;
  onClose: () => void;
}

export function GuidePublicReviewsDialog({
  profileId,
  guideName,
  open,
  onClose,
}: GuidePublicReviewsDialogProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: guidesKeys.reviews(profileId ?? '', { page: 1, limit: 10 }),
    queryFn: () => guidesApi.getReviews(profileId!, { page: 1, limit: 10 }),
    enabled: open && Boolean(profileId),
  });

  const summary = data?.summary;
  const reviews = data?.data ?? [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Reviews
          </Typography>
          {guideName && (
            <Typography variant="body2" color="text.secondary">
              {guideName}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {error && <Alert severity="error">Failed to load reviews.</Alert>}

        {summary && summary.reviewCount > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h4" fontWeight={800}>
                {summary.averageRating.toFixed(1)}
              </Typography>
              <Rating value={summary.averageRating} readOnly precision={0.1} />
              <Typography variant="body2" color="text.secondary">
                ({summary.reviewCount})
              </Typography>
            </Box>
            <RatingBreakdown summary={summary} />
          </Box>
        )}

        {!isLoading && reviews.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No reviews yet.
          </Typography>
        )}

        {reviews.map((review: Review) => (
          <GuideReviewListItem key={review.id} review={review} />
        ))}
      </DialogContent>
    </Dialog>
  );
}
